import { Component, OnInit, inject } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelectModule } from '@angular/material/select'
import { PageHeaderComponent } from '../../shared/page-header/page-header.component'
import {
  StaffDirectoryService,
  StaffMember,
  StationSummary,
} from '../../services/staff-directory.service'
import { SnackbarService } from '../../services/snackbar.service'

@Component({
  selector: 'app-staff-directory',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    PageHeaderComponent,
  ],
  templateUrl: './staff-directory.component.html',
  styleUrl: './staff-directory.component.css',
})
export class StaffDirectoryComponent implements OnInit {
  private staffDirectoryService = inject(StaffDirectoryService)
  private snackBar = inject(SnackbarService)

  viewMode: 'by-person' | 'by-station' = 'by-person'
  loading = false
  staff: StaffMember[] = []
  stationsSummary: StationSummary[] = []
  searchQuery = ''
  selectedRole = ''
  selectedStation = ''

  currentPage = 1
  itemsPerPage = 10
  totalStaff = 0
  totalPages = 0
  hasMore = false

  // By-station pager: independent of the staff pager (get_staff_directory stations_pagination).
  stationsCurrentPage = 1
  totalStations = 0
  stationsTotalPages = 0

  roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'owner', label: 'Owner' },
    { value: 'station_owner', label: 'Station Owner' },
    { value: 'station_user', label: 'Station User' },
  ]

  ngOnInit(): void {
    this.loadStaffDirectory()
  }

  loadStaffDirectory(): void {
    this.loading = true
    const offset = (this.currentPage - 1) * this.itemsPerPage
    const stationsOffset = (this.stationsCurrentPage - 1) * this.itemsPerPage

    this.staffDirectoryService
      .getStaffDirectory({
        role: this.selectedRole || undefined,
        search: this.searchQuery || undefined,
        limit: this.itemsPerPage,
        offset: offset,
        stations_limit: this.itemsPerPage,
        stations_offset: stationsOffset,
      })
      .subscribe({
        next: (res) => {
          this.staff = res.staff ?? []
          this.stationsSummary = res.stations_summary ?? []
          this.totalStaff = res.pagination?.total ?? res.total_staff ?? 0
          this.totalPages = res.pagination?.total_pages ?? 0
          this.hasMore = res.pagination?.has_more ?? false
          // Size the by-station pager from the stations count, not the staff count.
          this.totalStations =
            res.stations_pagination?.total ?? res.stations_total ?? this.stationsSummary.length
          this.stationsTotalPages =
            res.stations_pagination?.total_pages ?? Math.ceil(this.totalStations / this.itemsPerPage)
          this.loading = false
        },
        error: (err) => {
          console.error('Failed to load staff directory', err)
          this.snackBar.error('Failed to load staff directory. Please try again.')
          this.loading = false
        },
      })
  }

  onSearch(query: string): void {
    this.searchQuery = query
    this.currentPage = 1
    this.stationsCurrentPage = 1
    this.loadStaffDirectory()
  }

  onRoleFilter(role: string): void {
    this.selectedRole = role
    this.currentPage = 1
    this.stationsCurrentPage = 1
    this.loadStaffDirectory()
  }

  goToStationsPage(page: number): void {
    if (page < 1 || page > this.stationsTotalPages) return
    if (page === this.stationsCurrentPage) return
    this.stationsCurrentPage = page
    this.loadStaffDirectory()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  get stationsPaginationStart(): number {
    if (this.totalStations === 0) return 0
    return (this.stationsCurrentPage - 1) * this.itemsPerPage + 1
  }

  get stationsPaginationEnd(): number {
    return Math.min(this.stationsCurrentPage * this.itemsPerPage, this.totalStations)
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return
    if (page === this.currentPage) return
    this.currentPage = page
    this.loadStaffDirectory()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  get paginationStart(): number {
    if (this.totalStaff === 0) return 0
    return (this.currentPage - 1) * this.itemsPerPage + 1
  }

  get paginationEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalStaff)
  }

  getPaginationPages(current = this.currentPage, totalPages = this.totalPages): number[] {
    const pages: number[] = []
    const maxVisible = 5
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      const start = Math.max(1, current - 2)
      const end = Math.min(totalPages, start + maxVisible - 1)
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }
    return pages
  }

  switchView(mode: 'by-person' | 'by-station'): void {
    this.viewMode = mode
  }

  roleBadgeClass(role: string): string {
    const map: Record<string, string> = {
      owner: 'badge-owner',
      station_owner: 'badge-station-owner',
      station_user: 'badge-station-user',
    }
    return map[role] || 'badge-default'
  }

  roleBadgeLabel(role: string): string {
    const map: Record<string, string> = {
      owner: 'Owner',
      station_owner: 'Station Owner',
      station_user: 'Station User',
    }
    return map[role] || role
  }

  trackByUserId(_index: number, member: StaffMember): number {
    return member.user_id
  }

  trackByStationId(_index: number, station: StationSummary): number {
    return station.station_id
  }
}
